import React from 'react';
import {
  Autocomplete,
  Checkbox,
  TextField,
  Typography,
  Box,
  Chip
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const MultiSelectAutocomplete = ({
                                   options = [],
                                   selectedValues,
                                   onChange,
                                   label = 'Select Items',
                                   multiple = true,
                                   getOptionLabel = (option) => option.name,
                                   getOptionValue = (option) => option.id,
                                   placeholder = 'Select...',
                                   size = "medium",
                                   ...props
                                 }) => {
  const selectedValueArray = multiple
    ? options.filter(opt => selectedValues?.includes(getOptionValue(opt)))
    : options.find(opt => getOptionValue(opt) === selectedValues) || null;

  const allSelected =
    multiple &&
    options.length > 0 &&
    Array.isArray(selectedValues) &&
    selectedValues.length === options.length;

  const optionsWithSelectAll = multiple
    ? [{ id: '__select_all__', name: allSelected ? 'Deselect All' : 'Select All' }, ...options]
    : options;

  const handleChange = (event, newValue) => {
    if (multiple) {
      const isSelectAllClicked = newValue.some(
        (v) => getOptionValue(v) === '__select_all__'
      );

      if (isSelectAllClicked) {
        onChange(allSelected ? [] : options.map(getOptionValue));
      } else {
        onChange(newValue.map(getOptionValue));
      }
    } else {
      onChange(newValue ? getOptionValue(newValue) : null);
    }
  };

  return (
    <Box>
      {label && (
        <Typography
          color="secondary"
          fontSize="0.95rem"
          sx={{ flex: 1, mb: 0.5 }}
          fontWeight={400}
        >
          {label}
        </Typography>
      )}

      <Autocomplete
        multiple={multiple}
        options={optionsWithSelectAll}
        disableCloseOnSelect={multiple}
        getOptionLabel={getOptionLabel}
        value={selectedValueArray}
        onChange={handleChange}
        size={size}
        ListboxProps={{
          style: {
            maxHeight: 250,
            overflowY: 'auto'
          }
        }}
        renderTags={(value, getTagProps) => (
          <Box
            sx={{
              maxHeight: 200,
              overflowY: 'auto',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            {value.map((option, index) => (
              <Chip
                key={index}
                label={getOptionLabel(option)}
                size="small"
                {...getTagProps({ index })}
              />
            ))}
          </Box>
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            {multiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={
                  getOptionValue(option) === '__select_all__' ? allSelected : selected
                }
              />
            )}
            {getOptionLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            sx={{
              maxHeight: 250,
              overflowY: 'auto',
              '& .MuiAutocomplete-inputRoot': {
                maxHeight: 250,
                overflowY: 'auto',
              }
            }}
          />
        )}
        isOptionEqualToValue={(option, value) =>
          getOptionValue(option) === getOptionValue(value)
        }
        {...props}
      />
    </Box>
  );
};

export default MultiSelectAutocomplete;
